import { RefObject, useEffect, useState } from 'react';

/**
 * Small motion helpers for the tenant website template. Everything is
 * transform/opacity driven, runs off requestAnimationFrame / observers (no
 * animation library) and switches itself off for prefers-reduced-motion.
 */

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/**
 * True once the element has scrolled into view (stays true). Returns a
 * callback ref so it also works for elements that mount later (e.g. after a
 * skeleton is replaced by real content).
 */
export function useInView<T extends Element>({ threshold = 0.3, rootMargin = '0px' } = {}) {
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView || !node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, threshold, rootMargin, inView]);

  return [setNode, inView] as const;
}

/**
 * Counts from 0 to `target` (ease-out-expo) once `start` is true. Keeps the
 * target's decimal places, so "4.9" counts as 0.0 → 4.9 rather than snapping
 * to 5. Reduced motion shows the final value immediately.
 */
export function useCountUp(target: number, start: boolean, { duration = 1600, decimals = 0 } = {}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;
    if (prefersReducedMotion() || target === 0) {
      setValue(target);
      return;
    }
    const startedAt = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setValue(target * eased);
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start, duration]);

  return (start ? value : 0).toFixed(decimals);
}

/** Number of decimal places in a numeric string ("4.90" → 2, "500" → 0). */
export function decimalPlaces(value: string | number) {
  const match = String(value).match(/\.(\d+)/);
  return match ? match[1].length : 0;
}

/**
 * Subtle scroll parallax: writes `--tp-parallax` (px) onto the element while it
 * is on screen. CSS decides what moves (see `.tp-parallax`), so the hook never
 * touches layout and the effect is a single composited transform.
 */
export function useParallax(ref: RefObject<HTMLElement>, strength = 0.08) {
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return;

    let raf = 0;
    let visible = false;

    const update = () => {
      raf = 0;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      // -1 when the element's centre is at the bottom of the viewport, +1 at the top.
      const progress = (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2);
      const offset = Math.max(-1, Math.min(1, progress)) * rect.height * strength;
      el.style.setProperty('--tp-parallax', `${offset.toFixed(1)}px`);
    };
    const schedule = () => {
      if (visible && !raf) raf = requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      schedule();
    });
    observer.observe(el);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (raf) cancelAnimationFrame(raf);
      el.style.removeProperty('--tp-parallax');
    };
  }, [ref, strength]);
}
